import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Reveal } from "@/components/motion";
import { Button } from "@/components/ui/button";
import { ArrowUpRight, Check, Star } from "lucide-react";
import { SERVICES, PORTFOLIO, PORTFOLIO_CATEGORIES, PACKAGES, PACKAGE_ROWS, IMAGES, PRIMARY_SLUGS } from "@/lib/data";
import { useEnquiry } from "@/components/Enquiry";

/* ---------------- Services ---------------- */
export function Services() {
  const { open } = useEnquiry();
  const items = SERVICES.filter((s) => !PRIMARY_SLUGS.includes(s.slug));
  return (
    <section id="services" className="py-20 md:py-32 bg-background" data-testid="services">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div>
            <Reveal><p className="uppercase text-xs tracking-[0.25em] text-primary font-medium mb-3">Complete Interior Solutions</p></Reveal>
            <Reveal delay={0.05}><h2 className="font-display text-3xl md:text-5xl font-bold max-w-xl leading-tight">Beyond kitchens & wardrobes — end-to-end interiors</h2></Reveal>
          </div>
          <Reveal delay={0.1}><p className="text-muted-foreground max-w-sm text-sm md:text-base">Alongside our flagship modular kitchens and wardrobes, we deliver complete residential and commercial interiors.</p></Reveal>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
          {items.map((s, i) => (
            <Reveal key={s.slug} delay={(i % 4) * 0.06}>
              <div className="group rounded-2xl overflow-hidden border border-border bg-card hover-lift h-full flex flex-col" data-testid={`service-card-${s.slug}`}>
                <Link to={`/services/${s.slug}`} className="img-zoom block relative h-36">
                  <img src={s.image} alt={s.title} loading="lazy" className="w-full h-full object-cover" />
                </Link>
                <div className="p-4 flex flex-col flex-1">
                  <h3 className="font-display text-base mb-1 group-hover:text-primary transition-colors leading-tight">{s.title}</h3>
                  <p className="text-xs text-muted-foreground leading-relaxed line-clamp-2 flex-1">{s.desc}</p>
                  <div className="flex items-center gap-3 mt-3">
                    <Link to={`/services/${s.slug}`} data-testid={`service-view-${s.slug}`} className="text-xs font-medium text-foreground/70 hover:text-primary">Details</Link>
                    <button onClick={() => open({ project_type: s.title, source: `service-${s.slug}` })} data-testid={`service-enquire-${s.slug}`}
                      className="text-xs font-medium text-primary flex items-center gap-1 hover:gap-2 transition-all">
                      Enquire <ArrowUpRight className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- Before / After Slider ---------------- */
function BeforeAfter() {
  const [val, setVal] = useState(50);
  const ref = useRef(null);
  const [w, setW] = useState(0);
  useEffect(() => {
    const measure = () => setW(ref.current?.offsetWidth || 0);
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);
  return (
    <div ref={ref} className="relative w-full h-80 md:h-[30rem] rounded-2xl overflow-hidden select-none shadow-xl" data-testid="before-after">
      <img src="/ba-after.jpg" alt="After renovation" className="absolute inset-0 w-full h-full object-cover" draggable="false" />
      <div className="absolute inset-0 overflow-hidden" style={{ width: `${val}%` }}>
        <img src="/ba-before.jpg" alt="Before renovation" className="absolute inset-0 h-full object-cover max-w-none" style={{ width: w ? `${w}px` : "100%" }} draggable="false" />
      </div>
      <div className="absolute top-0 bottom-0 w-0.5 bg-white shadow-[0_0_8px_rgba(0,0,0,0.4)] pointer-events-none" style={{ left: `${val}%` }}>
        <span className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-10 h-10 rounded-full bg-white shadow-lg flex items-center justify-center text-primary text-sm font-bold">↔</span>
      </div>
      <input type="range" min="0" max="100" value={val} onChange={(e) => setVal(Number(e.target.value))} data-testid="ba-slider"
        className="absolute inset-0 w-full h-full opacity-0 cursor-ew-resize" aria-label="Before after comparison slider" />
    </div>
  );
}

/* ---------------- Portfolio ---------------- */
export function Portfolio() {
  const [cat, setCat] = useState("All");
  const items = cat === "All" ? PORTFOLIO : PORTFOLIO.filter((p) => p.category === cat);
  return (
    <section id="portfolio" className="py-20 md:py-32 bg-secondary/40" data-testid="portfolio">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <Reveal><p className="uppercase text-xs tracking-[0.25em] text-primary font-medium mb-3 text-center">Portfolio</p></Reveal>
        <Reveal delay={0.05}><h2 className="font-display text-3xl md:text-5xl font-bold text-center mb-8">Projects that speak luxury</h2></Reveal>

        <Reveal delay={0.1}>
          <div className="flex flex-wrap justify-center gap-2 mb-10">
            {PORTFOLIO_CATEGORIES.map((c) => (
              <button key={c} onClick={() => setCat(c)} data-testid={`portfolio-cat-${c}`}
                className={`px-4 py-1.5 rounded-full text-sm border transition-colors ${cat === c ? "bg-primary text-white border-primary" : "border-border hover:border-primary/50"}`}>
                {c}
              </button>
            ))}
          </div>
        </Reveal>

        <div className="columns-1 sm:columns-2 lg:columns-3 gap-5 [&>*]:mb-5">
          {items.map((p, i) => (
            <Reveal key={p.title + i} delay={(i % 3) * 0.06}>
              <div className="img-zoom relative rounded-2xl overflow-hidden group cursor-pointer break-inside-avoid" data-testid={`portfolio-item-${i}`}>
                <img src={p.image} alt={p.title} loading="lazy" className={`w-full object-cover ${i % 3 === 0 ? "h-80" : "h-64"}`} />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-end p-6">
                  <span className="text-gold text-xs uppercase tracking-wider">{p.category}</span>
                  <h3 className="font-display text-xl text-white">{p.title}</h3>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <div className="mt-16">
          <Reveal><h3 className="font-display text-2xl text-center mb-6">Before & After</h3></Reveal>
          <Reveal delay={0.1}><div className="max-w-3xl mx-auto"><BeforeAfter /></div></Reveal>
        </div>
      </div>
    </section>
  );
}

/* ---------------- Packages ---------------- */
export function Packages() {
  const { open } = useEnquiry();
  return (
    <section id="packages" className="py-20 md:py-32 bg-background" data-testid="packages">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <Reveal><p className="uppercase text-xs tracking-[0.25em] text-primary font-medium mb-3 text-center">Packages</p></Reveal>
        <Reveal delay={0.05}><h2 className="font-display text-3xl md:text-5xl font-bold text-center mb-14">Transparent pricing, premium value</h2></Reveal>

        <div className="grid md:grid-cols-3 gap-6">
          {PACKAGES.map((pkg, i) => (
            <Reveal key={pkg.name} delay={i * 0.1}>
              <div className={`relative rounded-3xl p-8 h-full flex flex-col border-2 hover-lift ${pkg.featured ? "border-gold bg-charcoal text-white shadow-2xl" : "border-border bg-card"}`} data-testid={`package-${pkg.name}`}>
                {pkg.featured && <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gold text-black text-xs font-semibold px-4 py-1 rounded-full uppercase tracking-wider">Most Popular</span>}
                <h3 className="font-display text-2xl mb-1">{pkg.name}</h3>
                <p className={`text-3xl font-display font-bold mb-6 ${pkg.featured ? "text-gold" : "text-primary"}`}>{pkg.price}</p>
                <ul className="space-y-3 flex-1">
                  {PACKAGE_ROWS.map((r) => (
                    <li key={r} className="flex items-start gap-2 text-sm">
                      <Check className={`w-4 h-4 mt-0.5 shrink-0 ${pkg.featured ? "text-gold" : "text-primary"}`} />
                      <span className={pkg.featured ? "text-white/80" : "text-muted-foreground"}><b className={pkg.featured ? "text-white" : "text-foreground"}>{r}:</b> {pkg.rows[r]}</span>
                    </li>
                  ))}
                </ul>
                <Button onClick={() => open({ project_type: "Full Home Interior", message: `Interested in the ${pkg.name} package`, source: `package-${pkg.name}` })}
                  data-testid={`package-quote-${pkg.name}`}
                  className={`rounded-full mt-8 ${pkg.featured ? "bg-gold text-black hover:bg-gold/90" : "bg-primary text-white hover:bg-primary/90"}`}>
                  Request Quote
                </Button>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
